import {
  formatFunnelAbandonmentAddress,
  hasUsableFunnelAddress,
} from '@/helpers/propertyValuation/funnelAbandonmentAddress';
import { isValuationFunnelPage, normalizeAppPath } from '@/helpers/Route/routeHelper';
import { getPropertyDetailsPagePath } from '@/helpers/Url/Url';
import { PropertyTypeNumber } from '@/models/valuationFunnel';
import { usePropertyDetailsStore } from '@/stores/propertyDetails/propertyDetails';
import { usePropertyValuationStore } from '@/stores/propertyValuation';
import {
  mapFormDataToHomeOwnerWorldPropertyFromValuationFunnel,
  normalizeFunnelAddress,
} from '@/stores/propertyValuation/services';
import {
  Address,
  HomeOwnerWorldPropertyFromValuationFunnelRequest,
} from '@/stores/types/propertyValuation';
import { TRACKING_LABEL } from '@/tracking/types';
import {
  MAX_BUILD_YEAR,
  MAX_LIVING_SPACE,
  MIN_BUILD_YEAR,
  ToastType,
  ValuationFlow,
} from '@/types';
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { RouteLocationNormalized, Router } from 'vue-router';

export type PendingNavigation =
  | { type: 'route'; to: RouteLocationNormalized }
  | { type: 'href'; href: string };

export const hasUsableFunnelRoomNumber = (rooms?: number): boolean => Boolean(rooms);

export const hasUsableFunnelLivingSpace = (livingSpace?: number): boolean =>
  typeof livingSpace === 'number' && livingSpace >= 1 && livingSpace <= MAX_LIVING_SPACE;

export const hasUsableFunnelBuildYear = (buildYear?: number): boolean =>
  typeof buildYear === 'number' && buildYear >= MIN_BUILD_YEAR && buildYear <= MAX_BUILD_YEAR;

export const hasUsableFunnelLotSize = (
  propertyType: PropertyTypeNumber | undefined,
  lotSize?: number | null,
): boolean => {
  if (propertyType !== PropertyTypeNumber.House) {
    return true;
  }

  return typeof lotSize === 'number' && lotSize > 0;
};

export interface FunnelAbandonmentPromptParams {
  routePath: string;
  formData: {
    leadId?: string;
    address: Parameters<typeof hasUsableFunnelAddress>[0];
    propertyType?: PropertyTypeNumber;
    rooms?: number;
    livingSpace?: number;
    buildYear?: number;
    lotSize?: number | null;
  };
  formStep: number;
  isAbandonmentPromptRegistered: boolean;
  isAbandonmentModalFeatureEnabled: boolean;
}

export interface FunnelAbandonmentPromptCriteria {
  isAbandonmentPromptRegistered: boolean;
  isAbandonmentModalFeatureEnabled: boolean;
  isOnValuationFunnelPage: boolean;
  hasNoLeadId: boolean;
  isPastStreetAndNumberStep: boolean;
  hasUsableAddress: boolean;
  hasPropertyType: boolean;
  hasUsableRoomNumber: boolean;
  hasUsableLivingSpace: boolean;
  hasUsableBuildYear: boolean;
  hasUsableLotSize: boolean;
  formStep: number;
  rooms: number | undefined;
  livingSpace: number | undefined;
  buildYear: number | undefined;
  lotSize: number | null | undefined;
  canShow: boolean;
}

export const getFunnelAbandonmentPromptCriteria = ({
  routePath,
  formData,
  formStep,
  isAbandonmentPromptRegistered,
  isAbandonmentModalFeatureEnabled,
}: FunnelAbandonmentPromptParams): FunnelAbandonmentPromptCriteria => {
  const isOnValuationFunnelPage = isValuationFunnelPage(normalizeAppPath(routePath));
  const hasNoLeadId = !formData.leadId;
  const isPastStreetAndNumberStep = formStep >= ValuationFlow.StreetAndNumber;
  const hasUsableAddress = hasUsableFunnelAddress(formData.address);
  const hasPropertyType = formData.propertyType !== undefined;
  const hasUsableRoomNumber = hasUsableFunnelRoomNumber(formData.rooms);
  const hasUsableLivingSpace = hasUsableFunnelLivingSpace(formData.livingSpace);
  const hasUsableBuildYear = hasUsableFunnelBuildYear(formData.buildYear);
  const hasUsableLotSize = hasUsableFunnelLotSize(formData.propertyType, formData.lotSize);

  const canShow =
    isAbandonmentPromptRegistered &&
    isAbandonmentModalFeatureEnabled &&
    isOnValuationFunnelPage &&
    hasNoLeadId &&
    isPastStreetAndNumberStep &&
    hasUsableAddress &&
    hasPropertyType &&
    hasUsableRoomNumber &&
    hasUsableLivingSpace &&
    hasUsableBuildYear &&
    hasUsableLotSize;

  return {
    isAbandonmentPromptRegistered,
    isAbandonmentModalFeatureEnabled,
    isOnValuationFunnelPage,
    hasNoLeadId,
    isPastStreetAndNumberStep,
    hasUsableAddress,
    hasPropertyType,
    hasUsableRoomNumber,
    hasUsableLivingSpace,
    hasUsableBuildYear,
    hasUsableLotSize,
    formStep,
    rooms: formData.rooms,
    livingSpace: formData.livingSpace,
    buildYear: formData.buildYear,
    lotSize: formData.lotSize,
    canShow,
  };
};

export const shouldShowFunnelAbandonmentPrompt = (params: FunnelAbandonmentPromptParams): boolean =>
  getFunnelAbandonmentPromptCriteria(params).canShow;

export const buildValuationFunnelAbandonmentPayload = (
  propertyValuationStore: ReturnType<typeof usePropertyValuationStore>,
  locale: HgSupportedLanguage,
): HomeOwnerWorldPropertyFromValuationFunnelRequest => {
  const selectedPropertyDetail =
    propertyValuationStore.propertyDetailsData?.[
      propertyValuationStore.selectedPropertyDetailIndex - 1
    ];

  return mapFormDataToHomeOwnerWorldPropertyFromValuationFunnel(
    propertyValuationStore.data,
    `${locale}-CH`,
    selectedPropertyDetail,
  );
};

export const getFunnelAbandonmentAddress = (
  propertyValuationStore: ReturnType<typeof usePropertyValuationStore>,
): Address => {
  return normalizeFunnelAddress(propertyValuationStore.data.address);
};

export const getFunnelAbandonmentFormattedAddress = (
  propertyValuationStore: ReturnType<typeof usePropertyValuationStore>,
): string => {
  return formatFunnelAbandonmentAddress(propertyValuationStore.data.address);
};

export const navigateToHomeOwnerWorldPropertyDetails = (
  propertyDetailsStore: ReturnType<typeof usePropertyDetailsStore>,
  router: Router,
  locale: HgSupportedLanguage,
  propertyId: string,
): void => {
  propertyDetailsStore.setToastType(ToastType.AddSuccess);

  const resolved = router.resolve({
    path: getPropertyDetailsPagePath(locale, propertyId),
    query: { source: TRACKING_LABEL.VALUATION_FUNNEL },
  });

  window.location.href = resolved.href;
};
